import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import './InterviewFeedback.css';

const InterviewFeedback = ({ onBack }) => {
  const { accessToken, sessionId, email, logout } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState('submit'); // 'submit', 'my-submissions', 'community'

  // Interviews state
  const [interviews, setInterviews] = useState([]);
  const [loadingInterviews, setLoadingInterviews] = useState(true);
  const [selectedInterview, setSelectedInterview] = useState(null);

  // Form state
  const [feedbackText, setFeedbackText] = useState('');
  const [difficultyLevel, setDifficultyLevel] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  // My submissions state
  const [mySubmissions, setMySubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  // Community questions state
  const [communityQuestions, setCommunityQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionFilters, setQuestionFilters] = useState({
    search: '',
    difficultyLevel: '',
    questionType: ''
  });

  // Load user's interviews on mount
  useEffect(() => {
    const loadInterviews = async () => {
      setLoadingInterviews(true);
      try {
        const data = await apiService.getMyInterviews(accessToken, sessionId, { limit: 50 });
        // Filter to only show completed interviews
        const completedInterviews = data.filter(i => i.status === 'completed' || i.status === 'analyzed');
        setInterviews(completedInterviews);
      } catch (error) {
        console.error('Failed to load interviews:', error);
      } finally {
        setLoadingInterviews(false);
      }
    };
    loadInterviews();
  }, [accessToken, sessionId]);

  // Load data when tab changes
  const loadMySubmissions = useCallback(async () => {
    setLoadingSubmissions(true);
    try {
      const data = await apiService.getMyFeedbackSubmissions(accessToken, sessionId);
      setMySubmissions(data?.feedbacks || []);
    } catch (error) {
      console.error('Failed to load submissions:', error);
      setMySubmissions([]);
    } finally {
      setLoadingSubmissions(false);
    }
  }, [accessToken, sessionId]);

  const loadCommunityQuestions = useCallback(async () => {
    setLoadingQuestions(true);
    try {
      const data = await apiService.getCommunityQuestions(accessToken, sessionId, questionFilters);
      setCommunityQuestions(data?.questions || []);
    } catch (error) {
      console.error('Failed to load community questions:', error);
      setCommunityQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  }, [accessToken, sessionId, questionFilters]);

  useEffect(() => {
    if (activeTab === 'my-submissions') {
      loadMySubmissions();
    } else if (activeTab === 'community') {
      loadCommunityQuestions();
    }
  }, [activeTab, loadMySubmissions, loadCommunityQuestions]);

  // Submit feedback
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    if (!selectedInterview) {
      setSubmitError('Please select an interview');
      setSubmitting(false);
      return;
    }

    if (!feedbackText.trim()) {
      setSubmitError('Please enter your feedback');
      setSubmitting(false);
      return;
    }

    try {
      const feedbackData = {
        interview_id: selectedInterview.id,
        feedback_text: feedbackText.trim(),
        difficulty_level: difficultyLevel
      };

      await apiService.submitInterviewFeedback(feedbackData, accessToken, sessionId);

      setSubmitSuccess('Interview feedback submitted successfully! It will be analyzed to generate questions.');

      // Reset form
      setSelectedInterview(null);
      setFeedbackText('');
      setDifficultyLevel('medium');

    } catch (error) {
      setSubmitError(error.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  // Load submission details
  const viewSubmissionDetails = async (feedbackId) => {
    try {
      const data = await apiService.getFeedbackSubmission(feedbackId, accessToken, sessionId);
      setSelectedSubmission(data);
    } catch (error) {
      console.error('Failed to load submission details:', error);
    }
  };

  // Search community questions
  const handleSearchQuestions = () => {
    loadCommunityQuestions();
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return '#4CAF50';
      case 'PROCESSING': return '#FF9800';
      case 'FAILED': return '#f44336';
      default: return '#9E9E9E';
    }
  };

  // Get difficulty badge color
  const getDifficultyColor = (level) => {
    switch (level?.toUpperCase()) {
      case 'EASY': return '#4CAF50';
      case 'MEDIUM': return '#FF9800';
      case 'HARD': return '#f44336';
      case 'EXPERT': return '#9C27B0';
      default: return '#9E9E9E';
    }
  };

  // Get question type badge color
  const getQuestionTypeColor = (type) => {
    switch (type?.toUpperCase()) {
      case 'MCQ': return '#2196F3';
      case 'CODING': return '#9C27B0';
      case 'BEHAVIORAL': return '#FF9800';
      case 'SITUATIONAL': return '#00BCD4';
      default: return '#607D8B';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="interview-feedback">
      <div className="feedback-container">
        <div className="feedback-header">
          <div>
            <h1>Interview Feedback</h1>
            <p className="subtitle">Share your interview experience to help generate practice questions</p>
          </div>
          <div className="header-actions">
            <span className="user-email">{email}</span>
            <button className="back-btn" onClick={onBack}>Back to Setup</button>
            <button className="logout-btn-feedback" onClick={logout}>Logout</button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-btn ${activeTab === 'submit' ? 'active' : ''}`}
            onClick={() => setActiveTab('submit')}
          >
            Submit Feedback
          </button>
          <button
            className={`tab-btn ${activeTab === 'my-submissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-submissions')}
          >
            My Submissions
          </button>
          <button
            className={`tab-btn ${activeTab === 'community' ? 'active' : ''}`}
            onClick={() => setActiveTab('community')}
          >
            Community Questions
          </button>
        </div>

        {/* Submit Feedback Tab */}
        {activeTab === 'submit' && (
          <div className="tab-content">
            {loadingInterviews ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading your interviews...</p>
              </div>
            ) : interviews.length === 0 ? (
              <div className="empty-state">
                <h3>No Completed Interviews</h3>
                <p>Complete an interview first to provide feedback about it.</p>
                <button className="primary-btn" onClick={onBack}>
                  Start an Interview
                </button>
              </div>
            ) : !selectedInterview ? (
              <div className="interview-selection">
                <h3>Select an Interview to Provide Feedback</h3>
                <p className="section-hint">Choose from your completed interviews below</p>

                <div className="interviews-grid">
                  {interviews.map((interview) => (
                    <div
                      key={interview.id}
                      className="interview-card"
                      onClick={() => setSelectedInterview(interview)}
                    >
                      <div className="card-header">
                        <h4>{interview.jobRoleTitle || 'Interview'}</h4>
                        <span
                          className="status-badge"
                          style={{ backgroundColor: '#4CAF50' }}
                        >
                          {interview.status}
                        </span>
                      </div>
                      <div className="card-body">
                        <p className="difficulty">Difficulty: {interview.difficulty || 'N/A'}</p>
                        <p className="date">Date: {formatDate(interview.createdAt)}</p>
                      </div>
                      <div className="card-footer">
                        <span className="select-link">Select this interview &rarr;</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="feedback-form">
                <div className="selected-interview-info">
                  <div className="info-header">
                    <h3>Providing Feedback For:</h3>
                    <button
                      type="button"
                      className="change-btn"
                      onClick={() => setSelectedInterview(null)}
                    >
                      Change Interview
                    </button>
                  </div>
                  <div className="interview-details">
                    <span className="job-title">{selectedInterview.jobRoleTitle}</span>
                    <span className="interview-date">{formatDate(selectedInterview.createdAt)}</span>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Your Interview Feedback</h3>
                  <p className="section-hint">
                    Describe your interview experience. Include details about questions asked, topics covered,
                    technical concepts discussed, and any other relevant information.
                  </p>

                  <div className="form-group">
                    <label htmlFor="feedback_text">Feedback *</label>
                    <textarea
                      id="feedback_text"
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      placeholder="Example: The interview started with questions about React hooks and state management. They asked me to explain useEffect dependencies and how to avoid infinite loops. Then we moved to system design where I had to design a real-time chat application. They also asked behavioral questions about handling conflicts in a team..."
                      rows={10}
                      required
                      disabled={submitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="difficulty_level">Overall Difficulty Level</label>
                    <select
                      id="difficulty_level"
                      value={difficultyLevel}
                      onChange={(e) => setDifficultyLevel(e.target.value)}
                      disabled={submitting}
                    >
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                      <option value="expert">Expert</option>
                    </select>
                  </div>
                </div>

                {submitError && (
                  <div className="error-message">{submitError}</div>
                )}

                {submitSuccess && (
                  <div className="success-message">{submitSuccess}</div>
                )}

                <div className="form-actions">
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* My Submissions Tab */}
        {activeTab === 'my-submissions' && (
          <div className="tab-content">
            {loadingSubmissions ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading your submissions...</p>
              </div>
            ) : mySubmissions.length === 0 ? (
              <div className="empty-state">
                <h3>No Submissions Yet</h3>
                <p>You haven't submitted any interview feedback yet. Share your experience to help others!</p>
                <button className="primary-btn" onClick={() => setActiveTab('submit')}>
                  Submit Your First Feedback
                </button>
              </div>
            ) : (
              <div className="submissions-grid">
                {selectedSubmission ? (
                  <div className="submission-detail">
                    <button
                      className="back-link"
                      onClick={() => setSelectedSubmission(null)}
                    >
                      &larr; Back to list
                    </button>

                    <div className="detail-header">
                      <h3>{selectedSubmission.job_title}</h3>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(selectedSubmission.processing_status) }}
                      >
                        {selectedSubmission.processing_status}
                      </span>
                    </div>

                    <div className="detail-meta">
                      <span><strong>Company:</strong> {selectedSubmission.company_name}</span>
                      <span><strong>Submitted:</strong> {formatDate(selectedSubmission.created_at)}</span>
                    </div>

                    <div className="feedback-text-section">
                      <h4>Your Feedback</h4>
                      <p>{selectedSubmission.feedback_text}</p>
                    </div>

                    {selectedSubmission.llm_analysis && (
                      <div className="analysis-section">
                        <h4>Analysis Summary</h4>
                        <p>{selectedSubmission.llm_analysis.analysis_summary}</p>

                        {selectedSubmission.extracted_skills?.length > 0 && (
                          <div className="tags-section">
                            <strong>Skills Identified:</strong>
                            <div className="tags">
                              {selectedSubmission.extracted_skills.map((skill, i) => (
                                <span key={i} className="tag skill-tag">{skill}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedSubmission.generated_questions?.length > 0 && (
                      <div className="generated-questions-section">
                        <h4>Generated Questions ({selectedSubmission.generated_questions.length})</h4>
                        {selectedSubmission.generated_questions.map((q, index) => (
                          <div key={index} className="generated-question">
                            <div className="question-header">
                              <span
                                className="type-badge"
                                style={{ backgroundColor: getQuestionTypeColor(q.question_type) }}
                              >
                                {q.question_type}
                              </span>
                              <span
                                className="difficulty-badge"
                                style={{ backgroundColor: getDifficultyColor(q.difficulty_level) }}
                              >
                                {q.difficulty_level}
                              </span>
                            </div>
                            <p className="question-text">{q.question}</p>
                            {q.expected_answer && (
                              <div className="expected-answer">
                                <strong>Expected Answer:</strong> {q.expected_answer}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="submissions-list">
                    {mySubmissions.map((submission) => (
                      <div
                        key={submission._id || submission.id}
                        className="submission-card"
                        onClick={() => viewSubmissionDetails(submission._id || submission.id)}
                      >
                        <div className="card-header">
                          <h4>{submission.job_title}</h4>
                          <span
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(submission.processing_status) }}
                          >
                            {submission.processing_status}
                          </span>
                        </div>
                        <div className="card-body">
                          <p className="company">{submission.company_name}</p>
                          <p className="feedback-preview">{submission.feedback_preview}</p>
                        </div>
                        <div className="card-footer">
                          <span className="date">
                            {formatDate(submission.created_at)}
                          </span>
                          <span className="view-link">View Details &rarr;</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Community Questions Tab */}
        {activeTab === 'community' && (
          <div className="tab-content">
            <div className="questions-filters">
              <div className="filter-row">
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={questionFilters.search}
                  onChange={(e) => setQuestionFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="search-input"
                />

                <select
                  value={questionFilters.difficultyLevel}
                  onChange={(e) => setQuestionFilters(prev => ({ ...prev, difficultyLevel: e.target.value }))}
                  className="filter-select"
                >
                  <option value="">All Difficulties</option>
                  <option value="EASY">Easy</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HARD">Hard</option>
                  <option value="EXPERT">Expert</option>
                </select>

                <select
                  value={questionFilters.questionType}
                  onChange={(e) => setQuestionFilters(prev => ({ ...prev, questionType: e.target.value }))}
                  className="filter-select"
                >
                  <option value="">All Types</option>
                  <option value="MCQ">MCQ</option>
                  <option value="SUBJECTIVE">Subjective</option>
                  <option value="CODING">Coding</option>
                  <option value="BEHAVIORAL">Behavioral</option>
                  <option value="SITUATIONAL">Situational</option>
                </select>

                <button
                  className="search-btn"
                  onClick={handleSearchQuestions}
                >
                  Search
                </button>
              </div>
            </div>

            {loadingQuestions ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading community questions...</p>
              </div>
            ) : communityQuestions.length === 0 ? (
              <div className="empty-state">
                <h3>No Questions Found</h3>
                <p>No community questions match your filters. Try adjusting your search criteria or submit feedback to add questions!</p>
              </div>
            ) : (
              <div className="questions-grid">
                {communityQuestions.map((question) => (
                  <div key={question._id || question.id} className="question-card">
                    <div className="question-card-header">
                      <span
                        className="type-badge"
                        style={{ backgroundColor: getQuestionTypeColor(question.question_type) }}
                      >
                        {question.question_type}
                      </span>
                      <span
                        className="difficulty-badge"
                        style={{ backgroundColor: getDifficultyColor(question.difficulty_level) }}
                      >
                        {question.difficulty_level}
                      </span>
                    </div>

                    <p className="question-text">{question.question}</p>

                    {question.expected_answer && (
                      <div className="answer-preview">
                        <strong>Expected Answer:</strong>
                        <p>{question.expected_answer.substring(0, 200)}...</p>
                      </div>
                    )}

                    <div className="question-tags">
                      {question.skill_tags?.slice(0, 3).map((tag, i) => (
                        <span key={i} className="tag skill-tag">{tag}</span>
                      ))}
                      {question.company_tags?.slice(0, 2).map((tag, i) => (
                        <span key={i} className="tag company-tag">{tag}</span>
                      ))}
                    </div>

                    <div className="question-meta">
                      <span>Used {question.usage_count || 0} times</span>
                      <span>{question.upvotes || 0} upvotes</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewFeedback;
